import React from 'react';
import { MotionConfig } from 'framer-motion';
import NotFoundPage from './pages/NotFoundPage';
import { CompaniesProvider } from './context/CompaniesContext';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { BrandingProvider } from './context/BrandingContext';
import { MainLayout } from './components/layout/MainLayout';
import { LoadingPage } from './components/common/LoadingSpinner';
import { GlobalMusicPlayer } from './components/music/GlobalMusicPlayer';
import { PageErrorBoundary } from './components/common/PageErrorBoundary';

const TelecomPage = React.lazy(() => import('./pages/TelecomPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const AssetsPage = React.lazy(() => import('./pages/AssetsPage'));
const AssetFormPage = React.lazy(() => import('./pages/AssetFormPage'));
const AssetDetailPage = React.lazy(() => import('./pages/AssetDetailPage'));
const PulsePage = React.lazy(() => import('./pages/PulsePage'));
const PulseDevicePage = React.lazy(() => import('./pages/PulseDevicePage'));
const PulseRemotePage = React.lazy(() => import('./pages/PulseRemotePage'));
const EmployeesPage = React.lazy(() => import('./pages/EmployeesPage'));
const EmployeeFormPage = React.lazy(() => import('./pages/EmployeeFormPage'));
const EmployeeDetailPage = React.lazy(() => import('./pages/EmployeeDetailPage'));
const InventoryPage = React.lazy(() => import('./pages/InventoryPage'));
const DisposedAssetsPage = React.lazy(() => import('./pages/DisposedAssetsPage'));
const VehicleFleetPage = React.lazy(() => import('./pages/VehicleFleetPage'));
const TransfersPage = React.lazy(() => import('./pages/TransfersPage'));
const SubscriptionsPage = React.lazy(() => import('./pages/SubscriptionsPage'));
const SubscriptionDetailPage = React.lazy(() => import('./pages/SubscriptionDetailPage'));
const CredentialVaultPage = React.lazy(() => import('./pages/CredentialVaultPage'));
const CredentialVaultDetailPage = React.lazy(() => import('./pages/CredentialVaultDetailPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const PersonalizationPage = React.lazy(() => import('./pages/PersonalizationPage'));

// Pages

// Protected Route Component
function ProtectedRoute({ children, requireAdmin = false, requireWrite = false }) {
  const { isAuthenticated, loading, isSuperAdmin, canWrite } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireWrite && !canWrite) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Public Route Component (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected Routes */}
      <Route path="/pulse/remote/:sessionId" element={<ProtectedRoute requireWrite><PulseRemotePage /></ProtectedRoute>} />
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        <Route path="/dashboard" element={<DashboardPage />} />
        
        {/* Assets */}
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/assets/new" element={<ProtectedRoute requireWrite><AssetFormPage /></ProtectedRoute>} />
        <Route path="/assets/:id" element={<AssetDetailPage />} />
        <Route path="/assets/:id/edit" element={<ProtectedRoute requireWrite><AssetFormPage /></ProtectedRoute>} />
        <Route path="/pulse" element={<PulsePage />} />
        <Route path="/pulse/:id" element={<PulseDevicePage />} />
        
        {/* Employees */}
        <Route path="/employees" element={<EmployeesPage />} />
        <Route path="/employees/new" element={<ProtectedRoute requireWrite><EmployeeFormPage /></ProtectedRoute>} />
        <Route path="/employees/:id" element={<EmployeeDetailPage />} />
        <Route path="/employees/:id/edit" element={<ProtectedRoute requireWrite><EmployeeFormPage /></ProtectedRoute>} />
        
        <Route path="/telecom" element={<TelecomPage />} />
        <Route path="/telecom/:id" element={<TelecomPage />} />

        {/* Inventory */}
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/disposed-assets" element={<DisposedAssetsPage />} />

        {/* Vehicle Fleet */}
        <Route path="/vehicles" element={<VehicleFleetPage />} />
        
        {/* Transfers */}
        <Route path="/transfers" element={<TransfersPage />} />
        
        {/* Subscriptions */}
        <Route path="/subscriptions" element={<SubscriptionsPage />} />
        <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />

        {/* Credential Vault */}
        <Route path="/credential-vault" element={<CredentialVaultPage />} />
        <Route path="/credential-vault/:id" element={<CredentialVaultDetailPage />} />

        <Route path="/personalization" element={<PersonalizationPage />} />

        {/* Settings - Admin Only */}
        <Route path="/settings" element={
          <ProtectedRoute requireAdmin>
            <SettingsPage />
          </ProtectedRoute>
        } />
      </Route>

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <BrandingProvider>
            <CompaniesProvider><MotionConfig reducedMotion="user"><PageErrorBoundary><React.Suspense fallback={<LoadingPage />}><AppRoutes /></React.Suspense></PageErrorBoundary></MotionConfig></CompaniesProvider>
            <GlobalMusicPlayer />
            <Toaster position="top-right" richColors />
          </BrandingProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
